// src/app/(landing)/page.tsx
"use client";

import React, { useState, useEffect, useRef } from 'react';
import HomeScript from "@/components/landing/HomeScript";
import HomeSearch from "@/components/landing/HomeSearch";
import Footer from "@/components/global/Footer";
import Link from "next/link";
import Image from "next/image";
import Tooltip from '@/components/global/Tooltip';
import Testimonials from "@/components/landing/Testimonials";
import RotatingImages from "@/components/landing/RotatingImages";

export default function Home() {
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoKey, setVideoKey] = useState(0);
  const [canPlayVideo, setCanPlayVideo] = useState(false);

  useEffect(() => {
    async function runSpeedTest() {
      try {
        const speed = (await Promise.race([testSpeed(), timeout(5000)])) as number; // kbps
        // Decide if we render a video at all
        if (speed >= 1500) {
          setCanPlayVideo(true);
          // pick quality (optional: ≥2000 -> ultra, else low)
          if (speed >= 2000) {
            setVideoSrc('/videos/ultra.webm');
          } else {
            setVideoSrc('/videos/low.webm');
          }
          setVideoKey(k => k + 1);
        } else {
          // Below 1.5 Mbps: image only, no video element
          setCanPlayVideo(false);
          setVideoSrc(null);
        }
      } catch (error) {
        console.error('Speed test failed:', error);
        // On failure: be conservative — image only
        setCanPlayVideo(false);
        setVideoSrc(null);
      } finally {
        setIsLoading(false);
      }
    }
    runSpeedTest();
  }, []);


  useEffect(() => {
    if (isLoading || !canPlayVideo) return;
    const el = videoRef.current;
    if (!el) return;

    const onCanPlay = () => {
      const p = el.play();
      if (p && typeof (p as any).catch === 'function') {
        (p as Promise<void>).catch(err => {
          console.warn('Autoplay was blocked; staying muted/inline.', err);
        });
      }
    };

    el.addEventListener('loadedmetadata', onCanPlay, { once: true });
    el.addEventListener('canplay', onCanPlay, { once: true });

    if (el.readyState >= 1) onCanPlay();

    return () => {
      el.removeEventListener('loadedmetadata', onCanPlay);
      el.removeEventListener('canplay', onCanPlay);
    };
  }, [isLoading, canPlayVideo, videoSrc]);

  const screenOneImages = [
    "/images/assets/screen_forbes.webp",
    "/images/assets/screen_fast-company.webp",
    "/images/assets/screen_success.webp",
    "/images/assets/screen_entrepreneur.webp",
    "/images/assets/screen_business_insider.webp",
    "/images/assets/screen_bloomberg.webp",
    "/images/assets/screen_usa_today.webp",
    "/images/assets/screen_variety.webp",
    "/images/assets/screen_VB.webp",
  ];

  const screenTwoImages = [
    "/images/assets/screen_mashable.webp",
    "/images/assets/screen_billboard.webp",
    "/images/assets/screen_mens-health.webp",
    "/images/assets/screen_elle.webp",
    "/images/assets/screen_TC.webp",
    "/images/assets/screen_WSJ.webp",
    "/images/assets/screen_vogue.webp",
    "/images/assets/screen_THR.webp",
    "/images/assets/screen_LA_Times.webp",
  ];

  return (
    <>
      <HomeScript />
      {/* Hero Section */}
      <div className="hero-banner-twelve lg-container" id="home">
        <div className="container">
          <div className="row">
            <div className="col-xl-11 col-lg-12 col-md-10 m-auto text-center">
              <h1 className="hero-heading font-gordita">
                World's #1 <span style={{ color: '#FF006B' }}>PR </span>
                <span style={{ color: '#FEDC00' }}>marketplace</span>
              </h1>
              <p className="hero-sub-heading">
                Get organic coverage on 1,000+ of media outlets with a refund guarantee!
              </p>
              <HomeSearch />
            </div>
          </div>

          <div className="screen-holder">
            {isLoading ? (
              // While probing speed, keep the background image to avoid layout shift.
              <Image
                src="/images/assets/screen_32_n-2_safari_2.webp"
                alt="Loading..."
                className="img-meta av-icons"
                width={2059}
                height={1170}
              />
            ) : canPlayVideo ? (
              // ≥ 1500 kbps: render video (no background image)
              <video
                key={videoKey}
                ref={videoRef}
                className="img-meta av-icons"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                style={{ width: '100%', height: 'auto' }}
                onError={(e) => {
                  const el = e.currentTarget;
                  console.error('Video failed to load:', {
                    srcTried: videoSrc,
                    networkState: el.networkState,
                    error: (el as any)?.error,
                  });
                }}
              >
                {/* Prefer WebM; MP4 as a wide-compat fallback */}
                {videoSrc ? <source src={videoSrc} type="video/webm" /> : null}
                <source src="/videos/vid-fin.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              // < 1500 kbps: image only, no video element at all
              <Image
                src="/images/assets/screen_32_n-2_safari_2.webp"
                alt="Background"
                className="img-meta av-icons"
                width={2059}
                height={1170}
              />
            )}

            {/* Keep rotating images and shapes regardless of speed */}
            <RotatingImages
              images={screenOneImages}
              intervalRange={[7000, 25000]}
              className="shapes screen-one av-icons"
              width={200}
              height={200}
              style={{ width: "13%", height: "auto" }}
            />
            <RotatingImages
              images={screenTwoImages}
              intervalRange={[7000, 25000]}
              className="shapes screen-two av-icons"
              width={200}
              height={200}
              style={{ width: "10%", height: "auto" }}
            />
            <Image
              src="/images/shape/230.svg"
              alt=""
              className="shapes shape-one av-icons"
              width={311}
              height={341}
            />
            <Image
              src="/images/shape/231.svg"
              alt=""
              className="shapes shape-two av-icons"
              width={172}
              height={172}
            />
          </div>
        </div>
        <Image
          src="/images/shape/232.svg"
          alt=""
          className="shapes shape-three av-icons"
          width={13}
          height={12}
        />
        <Image
          src="/images/shape/233.svg"
          alt=""
          className="shapes shape-four av-icons"
          width={17}
          height={20}
        />
        <Image
          src="/images/shape/234.svg"
          alt=""
          className="shapes shape-five av-icons"
          width={13}
          height={16}
        />
        <Image
          src="/images/shape/235.svg"
          alt=""
          className="shapes shape-six av-icons"
          width={14}
          height={14}
        />
        <Image
          src="/images/shape/236.svg"
          alt=""
          className="shapes shape-seven av-icons"
          width={36}
          height={30}
        />
        <Image
          src="/images/shape/232.svg"
          alt=""
          className="shapes shape-eight av-icons"
          width={13}
          height={12}
        />
      </div>

      {/* Counter Section */}
      <div className="counter-style-three lg-container mt-130">
        <div className="container">
          <div className="bottom-border">
            <div className="row">
              <div className="col-lg-4">
                <div className="counter-box-five text-center mb-65" data-aos="fade-up">
                  <div className="dot" style={{ background: '#25E5DA' }}></div>
                  <h2 className="number font-gordita">
                    <span className="timer" data-from="0" data-to="12" data-speed="1500" data-refresh-interval="5">12</span>k
                  </h2>
                  <p>Happy customers</p>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="counter-box-five text-center mb-65" data-aos="fade-up" data-aos-delay="100">
                  <div className="dot" style={{ background: '#FF36AF' }}></div>
                  <h2 className="number font-gordita">
                    <span className="timer" data-from="0" data-to="56" data-speed="1500" data-refresh-interval="5">56</span>k
                  </h2>
                  <p>Orders completed</p>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="counter-box-five text-center mb-65" data-aos="fade-up" data-aos-delay="150">
                  <div className="dot" style={{ background: '#FFB13A' }}></div>
                  <h2 className="number font-gordita">
                    <span className="timer" data-from="0" data-to="1000" data-speed="1500" data-refresh-interval="5">1000</span>+
                  </h2>
                  <p>Media outlets available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="fancy-feature-seventeen lg-container mt-150 md-mt-90" id="feature">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 m-auto" data-aos="fade-up">
              <div className="title-style-thirteen text-center mb-130 md-mb-90">
                <div className="upper-title">Features</div>
                <h2>
                  The easiest way to acquire <br />
                  <span>
                    top media
                    <Image src="/images/shape/line-shape-14.svg" className="y-line" alt="" width={100} height={100} />
                  </span> placements
                </h2>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="bottom-border pb-50">
            <div className="row">
              <div className="col-lg-4 col-sm-6" data-aos="fade-up" data-aos-duration="1200">
                <div className="block-meta">
                  <div className="icon d-flex align-items-end">
                    <Image src="/images/icon/94.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <h4>You Choose the Media</h4>
                  <p>With our platform, you’re in charge. Select the exact media outlet where you want to shine - from niche blogs to industry giants, the choice is yours!</p>
                </div>
              </div>
              <div className="col-lg-4 col-sm-6" data-aos="fade-up" data-aos-duration="1200" data-aos-delay="100">
                <div className="block-meta">
                  <div className="icon d-flex align-items-end">
                    <Image src="/images/icon/95.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <h4>Transparent Pricing</h4>
                  <p>No hidden fees, no surprises. Know your exact budget upfront. We offer fixed pricing for every publication to suit every PR strategy.</p>
                </div>
              </div>
              <div className="col-lg-4 col-sm-6" data-aos="fade-up" data-aos-duration="1200" data-aos-delay="200">
                <div className="block-meta">
                  <div className="icon d-flex align-items-end">
                    <Image src="/images/icon/96.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <h4>Guaranteed Results</h4>
                  <p>We stand by our promise. If we can’t secure your placement in the specified timeframe, you’ll receive a 100% refund — no questions asked.</p>
                </div>
              </div>
              <div className="col-lg-4 col-sm-6" data-aos="fade-up" data-aos-duration="1200">
                <div className="block-meta">
                  <div className="icon d-flex align-items-end">
                    <Image src="/images/icon/97.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <h4>Effortless Process</h4>
                  <p>Submit your request, sit back, and let our expert agents handle the rest. From pitching to publication, we’ve got your PR campaigns covered.</p>
                </div>
              </div>
              <div className="col-lg-4 col-sm-6" data-aos="fade-up" data-aos-duration="1200" data-aos-delay="100">
                <div className="block-meta">
                  <div className="icon d-flex align-items-end">
                    <Image src="/images/icon/98.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <h4>Full Control & Visibility</h4>
                  <p>Track your progress every step of the way. Our intuitive dashboard provides updates on your placement’s status in real time.</p>
                </div>
              </div>
              <div className="col-lg-4 col-sm-6" data-aos="fade-up" data-aos-duration="1200" data-aos-delay="200">
                <div className="block-meta">
                  <div className="icon d-flex align-items-end">
                    <Image src="/images/icon/99.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <h4>Build Credibility</h4>
                  <p>Get featured in media outlets that resonate with your target audience, boosting your brand authority and public trust.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Tour Section */}
      <div className="fancy-feature-twentyNine lg-container mt-140 pt-20 md-mt-70" id="p-tour">
        <div className="container">
          <div className="block-style-thirty">
            <div className="row">
              <div className="col-xl-5 col-lg-6" data-aos="fade-up">
                <div className="text-block md-pb-60">
                  <div className="feature">Who Is This For?</div>
                  <div className="title-style-nine mb-45 md-mb-30">
                    <h2>Why media placements matter?</h2>
                  </div>
                  <p>Whether you’re an individual looking to share your expertise, or a startup launching a new product - our platform is tailored to meet your unique PR needs.</p>
                  <ul className="feature-list-two">
                    <li>
                      <span>Build Credibility</span> - establish yourself as an industry leader by appearing in trusted outlets.
                    </li>
                    <li>
                      <span>Top SEO Rankings</span> - acquire authority backlinks that will boost your search engine rankings.
                    </li>
                    <li>
                      <span>Drive Sales</span> - leverage the power of earned media to increase conversions and revenue.
                    </li>
                  </ul>
                  <Link href="/marketplace" className="theme-btn-thirteen mt-30">Get Started</Link>
                </div>
              </div>
              <div className="col-xl-7 col-lg-6" data-aos="fade-up">
                <Image src="/images/assets/screen_X_media_3.webp" draggable="false" alt="" className="y-line noselect" width={758} height={817} />
              </div>
            </div>
          </div>

          <div className="mt-40 pb-100 md-pb-80 bottom-border">
            <div className="row justify-content-between">
              <div className="col-xl-3 col-md-4" data-aos="fade-up">
                <div className="block-style-twentyEight text-center mt-40">
                  <div className="icon d-flex justify-content-center align-items-end">
                    <Image src="/images/icon/160.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <h4>Control the Narrative</h4>
                  <p>With targeted placements, you decide how and where your message is told. Share your story where it matters most.</p>
                </div>
              </div>
              <div className="col-xl-3 col-md-4" data-aos="fade-up" data-aos-delay="100">
                <div className="block-style-twentyEight text-center mt-40">
                  <div className="icon d-flex justify-content-center align-items-end">
                    <Image src="/images/icon/161.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <h4>Boost Visibility</h4>
                  <p>Earned media in world's top media publications will help you reach millions of readers, listeners, and viewers worldwide.</p>
                </div>
              </div>
              <div className="col-xl-3 col-md-4" data-aos="fade-up" data-aos-delay="200">
                <div className="block-style-twentyEight text-center mt-40">
                  <div className="icon d-flex justify-content-center align-items-end">
                    <Image src="/images/icon/162.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <h4>Product Launches</h4>
                  <p>Media placements can amplify the reach and impact of your product launches, ensuring a successful market entry.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="fancy-feature-thirty mt-140 md-mt-100">
        <div className="container">
          <div className="row">
            <div className="col-xl-10 col-lg-9 m-auto" data-aos="fade-up">
              <div className="title-style-thirteen text-center mb-80 md-mb-60">
                <div className="upper-title">Step by Step</div>
                <h2>
                  <span>
                    How It Works
                    <Image src="/images/shape/line-shape-14.svg" className="y-line" alt="" width={100} height={100} />
                  </span>
                </h2>
              </div>
            </div>
          </div>
        </div>
        <div className="wrapper">
          <div className="container">
            <div className="row">
              <div className="col-md-6" data-aos="fade-up">
                <div className="block-style-thirtyTwo d-flex">
                  <div className="icon d-flex align-items-center justify-content-center" style={{ background: '#F7EDFF' }}>
                    <Image src="/images/icon/166.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <div className="text">
                    <h4>Step 1: Browse Publications</h4>
                    <p>Explore our database of 1,000+ publications. Use filters like industry, country, or DA/DR rating to pinpoint the perfect outlet.</p>
                    <Link href="#" className="theme-btn-ten">View marketplace <i className="fa fa-chevron-right" aria-hidden="true"></i></Link>
                  </div>
                </div>
              </div>
              <div className="col-md-6" data-aos="fade-up" data-aos-delay="100">
                <div className="block-style-thirtyTwo d-flex">
                  <div className="icon d-flex align-items-center justify-content-center" style={{ background: '#D8FFF1' }}>
                    <Image src="/images/icon/167.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <div className="text">
                    <h4>Step 2: Select & Submit</h4>
                    <p>Choose the publication that aligns with your goals. Share your story or idea through our easy-to-use checkout and wait for approval.</p>
                    <Link href="#" className="theme-btn-ten">Get started <i className="fa fa-chevron-right" aria-hidden="true"></i></Link>
                  </div>
                </div>
              </div>
              <div className="col-md-6" data-aos="fade-up">
                <div className="block-style-thirtyTwo d-flex">
                  <div className="icon d-flex align-items-center justify-content-center" style={{ background: '#E6FBFF' }}>
                    <Image src="/images/icon/168.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <div className="text">
                    <h4>Step 3: We Handle the Rest</h4>
                    <p>Our agents will liaise with the publication on your behalf. From negotiations to ensuring that the story aligns with both your needs and the editorial guidelines - we’ll manage every detail.</p>
                    <Link href="#" className="theme-btn-ten">Learn more <i className="fa fa-chevron-right" aria-hidden="true"></i></Link>
                  </div>
                </div>
              </div>
              <div className="col-md-6" data-aos="fade-up" data-aos-delay="100">
                <div className="block-style-thirtyTwo d-flex">
                  <div className="icon d-flex align-items-center justify-content-center" style={{ background: '#FFEBDB' }}>
                    <Image src="/images/icon/169.svg" alt="" className="av-icons" width={100} height={100} />
                  </div>
                  <div className="text">
                    <h4>Step 4: Get Published</h4>
                    <p>Monitor the whole process from the dashboard and see your story go live in the chosen outlet within the specified timeframe. If not, we’ll refund your money in full.</p>
                    <Link href="#" className="theme-btn-ten">Our guarantee <i className="fa fa-chevron-right" aria-hidden="true"></i></Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Image src="/images/shape/237.svg" alt="" className="shapes shape-one av-icons" width={100} height={100} />
          <Image src="/images/shape/238.svg" alt="" className="shapes shape-two av-icons" width={100} height={100} />
        </div>
      </div>

      {/* Testimonials Section */}
      <Testimonials />

      {/* CTA Section */}
      <div className="fancy-short-banner-fourteen mt-150 md-mt-80">
        <div className="container">
          <div className="row">
            <div className="col-12" data-aos="fade-up">
              <div className="title-style-thirteen">
                <h2>12,000+ Customers <br /> use Avelon PR. Try it now!</h2>
              </div>
              <p>Try it risk free — all orders are covered by a 100% guarantee.</p>
              <Link href="/auth/sign-up" className="theme-btn-twelve">Let’s Get Started</Link>
            </div>
          </div>
        </div>
        <Image src="/images/shape/239.svg" alt="" className="shapes shape-one av-icons" width={100} height={100} />
        <Image src="/images/shape/240.svg" alt="" className="shapes shape-two av-icons" width={100} height={100} />
        <Image src="/images/shape/232.svg" alt="" className="shapes shape-three av-icons" width={13} height={12} />
        <Image src="/images/shape/233.svg" alt="" className="shapes shape-four av-icons" width={17} height={20} />
        <Image src="/images/shape/234.svg" alt="" className="shapes shape-five av-icons" width={13} height={16} />
        <Image src="/images/shape/235.svg" alt="" className="shapes shape-six av-icons" width={14} height={14} />
        <Image src="/images/shape/236.svg" alt="" className="shapes shape-seven av-icons" width={36} height={30} />
        <Image src="/images/shape/232.svg" alt="" className="shapes shape-eight av-icons" width={13} height={12} />
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
}

// Speed Test Function (robust to missing Content-Length)
async function testSpeed() {
  const controller = new AbortController();
  const testFile = `/videos/test.png?t=${Date.now()}`; // Prevent caching
  const startTime = performance.now();
  try {
    const response = await fetch(testFile, { cache: 'no-store', signal: controller.signal });
    if (!response.ok) throw new Error('Failed to fetch test file');

    // Use actual bytes read; proxies/CDNs sometimes strip Content-Length
    const buf = await response.arrayBuffer();
    const bytes = buf.byteLength;
    if (!bytes) throw new Error('Test file size is zero');

    const duration = (performance.now() - startTime) / 1000; // seconds
    const speedBps = (bytes * 8) / duration; // bits per second
    const speedKbps = speedBps / 1000; // kilobits per second
    console.log(`Measured speed: ${bytes} bytes in ${duration.toFixed(2)}s -> ${speedKbps.toFixed(2)} kbps`);
    return speedKbps;
  } catch (error) {
    console.error('Speed test failed:', error);
    return 0; // Default to lowest quality
  } finally {
    controller.abort();
  }
}

// Timeout Utility
function timeout(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}